import React, { type HTMLAttributes, type ReactNode } from "react";

import { CheckIcon } from "@heroicons/react/outline";
import clsx from "clsx";
import Image, { type StaticImageData } from "next/image";

import Heading from "components/heading";
import HeroSection from "components/hero-section";

type FeatureBlockFigureProps = { src: StaticImageData; alt: string; className?: string };

export type FeatureBlockProps = {
  title: string;
  heading: ReactNode;
  description: ReactNode;
  items: string[];
  action: { href: string; label: string };
  image: FeatureBlockFigureProps;
};

export const FeatureBlockContainer = ({ className, ...rest }: HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx("space-y-6 md:space-y-10", className)} {...rest} />
);

export const FeatureBlockFigure = ({ src, alt, className }: FeatureBlockFigureProps) => (
  <figure className={clsx("flex-1 w-full mx-auto", className)}>
    <Image
      src={src}
      alt={`Enerflo Operating System: ${alt}`}
      layout="responsive"
      quality={100}
      placeholder="blur"
      className="crispImage"
      sizes="(min-width: 768px) 50vw, 100vw"
    />
  </figure>
);

type FeatureBlockHeaderProps = { className?: string; children: ReactNode; title: string; titleClassName?: string };

export const FeatureBlockHeader = ({ title, children, className, titleClassName }: FeatureBlockHeaderProps) => (
  <header className={clsx("mb-12", className)}>
    <HeroSection.Tagline className={clsx("mb-8", titleClassName)}>{title}</HeroSection.Tagline>
    <Heading tag="h2" size="h3" className="mb-6">
      {children}
    </Heading>
  </header>
);

export const ListBlock = ({ items, iconClassName }: { items: string[]; iconClassName?: string }) => (
  <ul className="space-y-3 inline-block mx-auto md:block md:mx-0">
    {items.map(item => (
      <li className="flex gap-2 text-left" key={item}>
        <div className={clsx("inline-block h-5 w-5 shrink-0", iconClassName)}>
          <CheckIcon />
        </div>
        <p>{item}</p>
      </li>
    ))}
  </ul>
);
